import {
  Shield,
  Lock,
  Smartphone,
  Database,
  Globe,
  AlertTriangle,
  FileText,
  CheckCircle,
  Server,
  Users,
  Eye,
  HardDrive,
  Wifi,
  Activity,
  GraduationCap,
  Award,
  Target,
  Landmark,
  Zap,
  Radio,
  BarChart,
  TrendingUp,
  Network,
  Map,
  PieChart,
  Layers,
  ClipboardCheck,
  Briefcase,
  Search,
  RefreshCw,
  LifeBuoy,
  MessageSquare,
  Cloud,
  Key,
} from 'lucide-react'

export const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Academy', path: '/academy' },
  { label: 'Risk Assessment', path: '/risk-assessment' },
  { label: 'Government', path: '/government' },
  { label: 'Organization', path: '/organization' },
  { label: 'Personal', path: '/personal' },
  { label: 'Tools', path: '/tools' },
  { label: 'Compliance', path: '/compliance' },
  { label: 'Downloads', path: '/downloads' },
  { label: 'About', path: '/about' },
]

// ... (Existing constants remain unchanged)

export const PERSONAL_STEPS = [
  'Use a password manager',
  'Enable Passkeys / MFA everywhere',
  'Secure primary email account',
  'Enable automatic OS & app updates',
  'Enable Full Disk Encryption (FDE)',
  'Implement 3-2-1 Backup Strategy',
  'Audit application permissions',
  'Lock SIM card & prevent SIM-swap',
  'Browser protection + Secure DNS',
  'Prepare a 24-hour response plan',
]

export const ORG_STEPS = [
  'Assign Security Ownership & Define Core Policies',
  'Maintain Asset & Data Inventory',
  'Enforce MFA for Critical Accounts',
  'Establish Patch & Update Management',
  'Implement Backup & Disaster Recovery (Test Restores)',
  'Enable Basic Logging & Alerting',
  'Apply Endpoint Hardening Baseline',
  'Secure Email Infrastructure (SPF, DKIM, DMARC)',
  'Prepare Incident Response Plan & Runbooks',
  'Assess Third-Party & Vendor Risks',
]

export const PILLARS = [
  {
    id: 'identity',
    title: 'Identity',
    icon: Users,
    description:
      'Secure your digital identity with strong authentication foundations.',
    checklist: ['Password Manager', 'MFA / Passkeys', 'Account Recovery'],
    link: '/personal/identity',
  },
  {
    id: 'devices',
    title: 'Devices',
    icon: Smartphone,
    description:
      'Harden your physical devices against theft and unauthorized access.',
    checklist: ['Full Disk Encryption', 'Auto-updates', 'Screen Lock'],
    link: '/devices/ios',
  },
  {
    id: 'communication',
    title: 'Communication',
    icon: Activity,
    description: 'Ensure your private conversations stay private.',
    checklist: ['E2E Encrypted Chat', 'Email Security', 'Metadata Awareness'],
    link: '/personal/communication',
  },
  {
    id: 'data',
    title: 'Data & Backup',
    icon: Database,
    description: 'Protect your data from loss, corruption, and ransomware.',
    checklist: ['3-2-1 Backup Rule', 'Cloud Encryption', 'Offline Backup'],
    link: '/personal/data-backup',
  },
  {
    id: 'internet',
    title: 'Internet Safety',
    icon: Globe,
    description:
      'Navigate the web without leaving a trail or getting infected.',
    checklist: ['Secure DNS', 'VPN Usage', 'Browser Hardening'],
    link: '/personal/internet',
  },
  {
    id: 'ir',
    title: 'Incident Response',
    icon: AlertTriangle,
    description: 'Know exactly what to do when things go wrong.',
    checklist: ['Account Takeover Plan', 'Lost Device Protocol', 'Fraud Alert'],
    link: '/personal/incident-response',
  },
]

export const TOOLS = [
  {
    category: 'Breach Check',
    items: [
      {
        name: 'Have I Been Pwned',
        description:
          'Check if your email or phone number has been in a data breach.',
        url: '/tools/breach-check',
        safetyNote: 'Official, safe to use. Uses k-anonymity for passwords.',
      },
      {
        name: 'Mozilla Monitor',
        description: 'Check for breaches and get mitigation guidance.',
        url: '/tools/breach-check',
        safetyNote: 'Operated by Mozilla. Safe and privacy-focused.',
      },
    ],
  },
  {
    category: 'Password Safety',
    items: [
      {
        name: 'HIBP Pwned Passwords',
        description:
          'Check if a password has been exposed without revealing it.',
        url: '/tools/password-safety',
        safetyNote: 'Safe. Uses k-anonymity API to protect your input.',
      },
      {
        name: 'Google Password Checkup',
        description: 'Audit saved passwords for weakness and breaches.',
        url: '/tools/password-safety',
        safetyNote: 'Official Google tool. Requires Google account.',
      },
    ],
  },
  {
    category: 'Account Security',
    items: [
      {
        name: 'Google Security Checkup',
        description: 'Review security settings and connected devices.',
        url: 'https://myaccount.google.com/intro/security-checkup',
        safetyNote: 'Official Google tool.',
      },
    ],
  },
]

export const COMPLIANCE_DATA = {
  global: {
    name: 'Global Baseline',
    overview: 'General best practices based on ISO/IEC 27001 and NIST CSF.',
    obligations: [
      'Protect personal data integrity and confidentiality',
      'Implement risk-based security controls',
      'Notify affected parties of serious breaches',
      'Maintain records of processing activities',
    ],
    link: '/compliance/global',
  },
  indonesia: {
    name: 'Indonesia (UU PDP)',
    overview: 'Undang-Undang Perlindungan Data Pribadi (UU No. 27 Tahun 2022).',
    obligations: [
      'Consent must be explicit and recorded',
      'Data subjects have rights to access, correct, and delete',
      'Data Protection Officer (DPO) required for large scale processing',
      'Breach notification within 3x24 hours',
    ],
    link: '/compliance/indonesia',
  },
}

// Checklists
export const IDENTITY_CHECKLIST = [
  'Password manager installed & active',
  'MFA / Passkey enabled on primary accounts',
  'Primary email secured (MFA + Recovery)',
  'Recovery codes saved offline',
  'Unique passwords for every service',
]

export const IOS_CHECKLIST = [
  'iOS updated to latest version',
  'Face ID / Touch ID enabled',
  'Passcode is 6+ digits or alphanumeric',
  'Find My iPhone enabled',
  'App permissions audited',
]

export const ANDROID_CHECKLIST = [
  'Security patch level is current',
  'Strong screen lock (Pattern/PIN/Bio)',
  'Google Play Protect enabled',
  'Install from unknown sources disabled',
  'Find My Device enabled',
]

export const WINDOWS_CHECKLIST = [
  'BitLocker encryption enabled',
  'Using Standard User account (not Admin)',
  'Windows Defender active & updated',
  'Windows Update set to automatic',
  'Uninstall unused bloatware',
]

export const ORG_PROTECT_CHECKLIST = [
  'MFA enforced for all users',
  'Patch management policy defined',
  'Backup system active & tested',
  'Endpoint protection deployed',
  'Least privilege access applied',
]

export const IR_ACCOUNT_CHECKLIST = [
  'Isolate compromised account',
  'Revoke all active sessions',
  'Change password immediately',
  'Enable/Reset MFA',
  'Check for forwarding rules',
]

export const MACOS_CHECKLIST = [
  'FileVault encryption enabled',
  'Automatic OS updates enabled',
  'Screen lock set to 5 mins or less',
  'Gatekeeper active (App Store/Identified Developers)',
  'Find My Mac enabled',
]

export const LINUX_CHECKLIST = [
  'Full disk encryption (LUKS) active',
  'Sudo policy configured (no NOPASSWD)',
  'Unattended upgrades enabled',
  'Firewall (ufw/iptables) active',
  'SSH hardened (Key auth only)',
]

export const INDONESIA_COMPLIANCE_CHECKLIST = [
  'Complete personal data inventory',
  'Data classification (General vs Specific)',
  'MFA active for sensitive data access',
  'Regular backups & restore testing',
  'Incident Response Plan available',
  'Breach notification procedure (3x24 hours)',
]

export const GLOBAL_COMPLIANCE_CHECKLIST = [
  'Security Policy Framework established',
  'Risk Assessment completed',
  'Asset Inventory up to date',
  'Access Controls (MFA/Least Privilege)',
  'Continuous Monitoring active',
  'Incident Response Plan tested',
]

export const GDPR_COMPLIANCE_CHECKLIST = [
  'Record of Processing Activities (ROPA)',
  'Data Protection Impact Assessment (DPIA)',
  'Encryption (At-rest & In-transit)',
  'Breach Notification Procedure (72h)',
  'Data Subject Access Request process',
  'DPO appointed (if required)',
]

export const SINGAPORE_COMPLIANCE_CHECKLIST = [
  'Consent obtained for data collection',
  'Data Inventory & Classification',
  'Protection measures implemented',
  'Breach Notification Plan ready',
  'Data Retention Policy defined',
  'DNC Registry checks (if marketing)',
]

export const BRAZIL_COMPLIANCE_CHECKLIST = [
  'Legal basis for processing documented',
  'Data Protection Officer (DPO) appointed',
  'Security measures implemented',
  'Incident Response Plan ready',
  'Data Inventory & Mapping complete',
  'Data Subject Rights process defined',
]

// Government Checklists
export const GOV_GOVERNANCE_CHECKLIST = [
  'Role structure (CISO, DPO, CSIRT) documented',
  'Security policy approved by leadership',
  'Risk register available & updated',
  'CSIRT active & registered',
  'Annual incident simulation conducted',
]

export const GOV_CIP_CHECKLIST = [
  'Complete critical systems inventory',
  'Network segmentation (IT vs OT) implemented',
  'Limited & audited access (Least Privilege)',
  'Offline backup available & tested',
  'Manual procedures (fallback) available',
]

export const GOV_OT_CHECKLIST = [
  'Passive monitoring active',
  'Jump server for remote access',
  'Application whitelisting active',
  'Strict change management',
  'Physical access secured',
]

export const GOV_IR_CHECKLIST = [
  'Incident Commander appointed',
  'Secure communication channels available',
  'National escalation procedure ready',
  'Public communication draft ready',
  'Legal review in the loop',
]

export const GOV_DATA_SOV_CHECKLIST = [
  'Data storage location identified',
  'National access control ensured',
  'Vendors subject to national law',
  'Vendor exit strategy available',
  'Strategic dependencies mapped',
]

export const GOV_VENDOR_CHECKLIST = [
  'Security due diligence conducted',
  'Security SLA in contract',
  'Audit rights guaranteed',
  'Incident notification clause included',
  'Data return plan available',
]

export const GOV_COMPLIANCE_ID_CHECKLIST = [
  'Data classification per regulations',
  'Access logs stored & audited',
  'Incident reporting to BSSN/Kominfo',
  'Regular security audits (KAMI Index)',
  'ISO 27001 certification (if mandatory)',
]

// Organization Checklists (NIST CSF)
export const ORG_GOVERN_CHECKLIST = [
  'Security Owner appointed',
  'Risk appetite documented',
  'Security policy approved by leadership',
  'Roles and responsibilities defined',
  'Oversight process established',
]

export const ORG_IDENTIFY_CHECKLIST = [
  'Asset inventory complete and up-to-date',
  'Data classified (Public/Internal/Confidential)',
  'Risk assessment conducted annually',
  'Risk register maintained',
  'Critical assets identified',
]

export const ORG_DETECT_CHECKLIST = [
  'Logging enabled on critical systems',
  'Logs centralized and retained',
  'Alerts configured for critical events',
  'Monitoring process defined',
  'Detection capabilities tested',
]

export const ORG_RESPOND_CHECKLIST = [
  'Incident response plan documented',
  'Incident Commander appointed',
  'Communication plan defined',
  'Tabletop exercise conducted',
  'Incident response tested',
]

export const ORG_RECOVER_CHECKLIST = [
  'Business Continuity Plan documented',
  'Disaster Recovery Plan documented',
  'RTO/RPO defined',
  'Recovery procedures tested',
  'Communication plan ready',
]

// Personal Checklists
export const COMMUNICATION_CHECKLIST = [
  'E2E encrypted messaging app installed',
  'Primary email secured with MFA',
  'Email forwarding rules audited',
  'Encrypted calling app available',
  'Contacts educated on secure communication',
]

export const DATA_BACKUP_CHECKLIST = [
  '3-2-1 backup rule implemented',
  'Automatic backups configured',
  'Cloud backups encrypted',
  'Offline backup created',
  'Restore tested successfully',
]

export const INTERNET_SAFETY_CHECKLIST = [
  'Secure DNS configured',
  'Browser privacy extensions installed',
  'Third-party cookies disabled',
  'VPN available for public Wi-Fi',
  'Privacy-focused search engine set as default',
]

export const PERSONAL_IR_CHECKLIST = [
  'Recovery codes saved offline',
  'Emergency contacts list ready',
  'Backup of critical data available',
  'Device tracking enabled',
  'Password manager with secure master password',
]

// Academy Data
export const COURSE_TRACKS = [
  {
    id: 'personal',
    title: 'Personal Security Track',
    description:
      'Cybersecurity certification for individuals and professionals.',
    duration: '8-12 hours',
    modules: 5,
    icon: Users,
    link: '/academy/personal',
  },
  {
    id: 'org',
    title: 'Organization Track',
    description:
      'Cybersecurity certification for organizations, SMEs, and institutions.',
    duration: '12-16 hours',
    modules: 4,
    icon: Shield,
    link: '/academy/organization',
  },
  {
    id: 'high-risk',
    title: 'High-Risk Profile Track',
    description:
      'Certification for journalists, activists, executives, and high-risk individuals.',
    duration: '10-14 hours',
    modules: 4,
    icon: Target,
    link: '/academy/high-risk',
  },
]

export const PERSONAL_MODULES = [
  {
    id: 1,
    title: 'Foundations of Cyber Security',
    topics: [
      'Threat landscape modern',
      'Human as attack surface',
      'Zero Trust mindset',
    ],
    objectives: [
      'Understand modern threats',
      'Recognize social engineering',
      'Apply Zero Trust principles',
    ],
  },
  {
    id: 2,
    title: 'Identity & Account Security',
    topics: [
      'Password vs Passkey',
      'MFA implementation',
      'Email as root of trust',
    ],
    objectives: [
      'Implement password manager',
      'Enable MFA/Passkeys',
      'Secure primary email',
    ],
  },
  {
    id: 3,
    title: 'Device Security',
    topics: [
      'OS Hardening (iOS/Android/Win/Mac)',
      'Disk encryption',
      'Update strategy',
    ],
    objectives: [
      'Enable full disk encryption',
      'Configure auto-updates',
      'Harden device settings',
    ],
  },
  {
    id: 4,
    title: 'Data & Backup',
    topics: ['3-2-1 strategy', 'Encryption', 'Cloud vs offline'],
    objectives: [
      'Implement 3-2-1 backup',
      'Encrypt backups',
      'Test restore procedures',
    ],
  },
  {
    id: 5,
    title: 'Incident Response (Personal)',
    topics: ['Account takeover', 'Lost device', 'SIM swap'],
    objectives: [
      'Execute 24-hour response plan',
      'Secure compromised accounts',
      'Prevent future incidents',
    ],
  },
]

// Threat Landscape Data
export const THREAT_ACTORS = [
  {
    name: 'Nation-State',
    motivation: 'Geopolitics, Intelligence, Disruption',
    target: 'Government, Energy, Telecommunications',
    capability: 'Advanced (APT)',
    risk: 'High',
  },
  {
    name: 'Cybercrime',
    motivation: 'Financial',
    target: 'Finance, Citizen Data, Public Services',
    capability: 'Medium-High',
    risk: 'High',
  },
  {
    name: 'Hacktivism',
    motivation: 'Ideology, Politics',
    target: 'Government Websites, Public Services',
    capability: 'Low-Medium',
    risk: 'Medium',
  },
  {
    name: 'Insider Threat',
    motivation: 'Negligence, Personal, Coercion',
    target: 'Internal Data, Critical Systems',
    capability: 'Variable',
    risk: 'High',
  },
  {
    name: 'Supply Chain',
    motivation: 'Access to downstream targets',
    target: 'IT Vendors, Software Dependencies',
    capability: 'High',
    risk: 'High',
  },
]

export const THREAT_CONTROL_MAPPING = [
  {
    threat: 'Phishing',
    control: 'MFA, Email Security, Training',
    function: 'PROTECT',
    standard: 'CIS 14',
  },
  {
    threat: 'Ransomware',
    control: 'Offline Backup, EDR, Segmentation',
    function: 'RECOVER',
    standard: 'CIS 11',
  },
  {
    threat: 'Insider',
    control: 'Least Privilege, Logging, Monitoring',
    function: 'DETECT',
    standard: 'CIS 6',
  },
  {
    threat: 'Supply Chain',
    control: 'Vendor Security, SLA, Audit Rights',
    function: 'IDENTIFY',
    standard: 'CIS 15',
  },
  {
    threat: 'OT Attack',
    control: 'Segmentation, Jump Server, Monitoring',
    function: 'PROTECT',
    standard: 'CIS 12',
  },
  {
    threat: 'Credential Theft',
    control: 'MFA, PAM, Password Policy',
    function: 'PROTECT',
    standard: 'CIS 5',
  },
]

// Global South Threat Data
export const GLOBAL_SOUTH_ACTORS = [
  {
    name: 'State-Sponsored (Proxy)',
    motivation: 'Intelijen, Pengaruh Geopolitik',
    target: 'Government, Strategic Industries',
    capability: 'Advanced',
    regionalNote: 'Often use proxy actors or commercial tools',
  },
  {
    name: 'Transnational Cybercrime',
    motivation: 'Finansial (Ransomware, Scam)',
    target: 'Countries with weak controls, SMEs',
    capability: 'Medium',
    regionalNote: 'Targets countries with limited law enforcement',
  },
  {
    name: 'Regional Hacktivism',
    motivation: 'Political, Social Conflict',
    target: 'Government, Media',
    capability: 'Low-Medium',
    regionalNote: 'Tied to regional political tensions',
  },
  {
    name: 'Insider & Human Risk',
    motivation: 'Lack of training, Economic pressure',
    target: 'Internal Systems',
    capability: 'Low',
    regionalNote: 'Higher risk due to limited awareness',
  },
  {
    name: 'Vendor & Supply Chain',
    motivation: 'Access to downstream targets',
    target: 'Foreign MSPs, Cloud Providers',
    capability: 'High',
    regionalNote: 'Heavy reliance on foreign vendors',
  },
]

export const GLOBAL_SOUTH_CONTROL_MAPPING = [
  {
    threat: 'Phishing & Account Takeover',
    control: 'MFA, Email Security',
    quickWin: 'MFA Rollout',
    longTerm: 'Security Awareness Program',
  },
  {
    threat: 'Ransomware',
    control: 'Backup Offline, EDR',
    quickWin: 'Offline Backups',
    longTerm: 'EDR Deployment',
  },
  {
    threat: 'Insider',
    control: 'Least Privilege, Logging',
    quickWin: 'Access Review',
    longTerm: 'PAM Implementation',
  },
  {
    threat: 'Vendor Risk',
    control: 'Due Diligence, SLA',
    quickWin: 'Vendor Inventory',
    longTerm: 'Security Audits',
  },
  {
    threat: 'Cloud Abuse',
    control: 'Baseline Hardening',
    quickWin: 'Config Review',
    longTerm: 'CSPM Tool',
  },
  {
    threat: 'OT Risk',
    control: 'Segmentation, Monitoring',
    quickWin: 'Network Mapping',
    longTerm: 'Jump Servers',
  },
]

// CMI Data
export const CMI_DOMAINS = [
  {
    id: 'governance',
    name: 'Governance & Leadership',
    weight: 20,
    description:
      'Leadership, policies, and organizational cybersecurity structure.',
    indicators: [
      'National Strategy',
      'CISO/CSIRT Structure',
      'Policy Framework',
      'Budget Allocation',
    ],
  },
  {
    id: 'risk',
    name: 'Risk & Asset Management',
    weight: 15,
    description:
      'Critical asset identification and data-driven risk management.',
    indicators: [
      'Asset Inventory',
      'Data Classification',
      'Risk Register',
      'Regular Assessment',
    ],
  },
  {
    id: 'identity',
    name: 'Identity & Access Security',
    weight: 15,
    description: 'Digital identity management and access control.',
    indicators: [
      'MFA Implementation',
      'Privileged Access (PAM)',
      'Identity Lifecycle',
      'Email Security',
    ],
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure & Resilience',
    weight: 20,
    description: 'Technical infrastructure resilience and disaster recovery.',
    indicators: [
      'Network Segmentation',
      'Offline Backups',
      'Disaster Recovery',
      'Manual Fallback',
    ],
  },
  {
    id: 'detection',
    name: 'Detection & Incident Response',
    weight: 20,
    description: 'Capability to detect and respond to cyber incidents.',
    indicators: [
      'Logging & Monitoring',
      'SOC Capability',
      'Incident Playbooks',
      'Regular Drills',
    ],
  },
  {
    id: 'ecosystem',
    name: 'Ecosystem & Supply Chain',
    weight: 10,
    description: 'Supply chain security and ecosystem collaboration.',
    indicators: [
      'Vendor Risk Management',
      'Cloud Governance',
      'Regional Cooperation',
      'Software Security',
    ],
  },
]

export const CMI_LEVELS = [
  { level: 1, name: 'Initial', desc: 'Ad-hoc, reactive, unstructured.' },
  {
    level: 2,
    name: 'Basic',
    desc: 'Basic controls exist, policies beginning to form.',
  },
  {
    level: 3,
    name: 'Managed',
    desc: 'Processes defined, consistent, and documented.',
  },
  {
    level: 4,
    name: 'Integrated',
    desc: 'Cross-sector coordination, proactive approach.',
  },
  {
    level: 5,
    name: 'Adaptive',
    desc: 'Intelligence-driven, continuous improvement.',
  },
]

// NIST CSF Functions
export const NIST_CSF_FUNCTIONS = [
  {
    id: 'govern',
    name: 'GOVERN',
    desc: 'Organizational context, risk management strategy, roles & responsibilities.',
    icon: Landmark,
    link: '/organization/govern',
  },
  {
    id: 'identify',
    name: 'IDENTIFY',
    desc: 'Asset management, risk assessment, supply chain risk management.',
    icon: Search,
    link: '/organization/identify',
  },
  {
    id: 'protect',
    name: 'PROTECT',
    desc: 'Identity management, data security, platform security, training.',
    icon: Shield,
    link: '/organization/protect',
  },
  {
    id: 'detect',
    name: 'DETECT',
    desc: 'Continuous monitoring, anomaly detection, adverse event analysis.',
    icon: Eye,
    link: '/organization/detect',
  },
  {
    id: 'respond',
    name: 'RESPOND',
    desc: 'Incident management, analysis, mitigation, reporting.',
    icon: AlertTriangle,
    link: '/organization/respond',
  },
  {
    id: 'recover',
    name: 'RECOVER',
    desc: 'Recovery planning, improvements, communications.',
    icon: RefreshCw,
    link: '/organization/recover',
  },
]
